import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { Brand, BrandPriority } from '../types';

export interface BrandListFilters {
  priority?: BrandPriority;
}

const brandsKey = (filters?: BrandListFilters) => ['brands', filters ?? {}] as const;
const brandKey = (id: string) => ['brand', id] as const;

const listBrands = async (filters?: BrandListFilters): Promise<Brand[]> => {
  const params = new URLSearchParams();
  if (filters?.priority) params.set('priority', filters.priority);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const res = await axios.get(`api/brands/${suffix}`);
  return res.data;
};

const getBrand = async (id: string): Promise<Brand> => {
  const res = await axios.get(`api/brands/${id}/`);
  return res.data;
};

export const useBrands = (filters?: BrandListFilters) =>
  useQuery({ queryKey: brandsKey(filters), queryFn: () => listBrands(filters) });

export const useBrand = (id: string | undefined) =>
  useQuery({ queryKey: brandKey(id ?? ''), queryFn: () => getBrand(id as string), enabled: !!id });

export interface CreateBrandInput {
  id: string;
  name: string;
  country: string;
  /** Hidden in the UI but kept on the model so the field can be re-surfaced later. */
  priority?: BrandPriority;
  color: string;
  accent?: string;
  logo?: string;
  permanent_discount?: string;
  /** Vendor spelling in the PIM catalog — used by the Pricy integration. */
  pim_vendor?: string;
  /** Brand identity card fields — shared with Dory. */
  website_url?: string;
  media_bank_url?: string;
  media_bank_access?: string;
}

export const useCreateBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateBrandInput): Promise<Brand> => {
      const res = await axios.post('api/brands/', payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brands'] }),
  });
};

export interface UpdateBrandInput {
  id: string;
  patch: Partial<Brand>;
}

export const useUpdateBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: UpdateBrandInput): Promise<Brand> => {
      const res = await axios.patch(`api/brands/${id}/`, patch);
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['brands'] });
      qc.setQueryData(brandKey(data.id), data);
    },
  });
};

export const useDeleteBrand = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`api/brands/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brands'] }),
  });
};

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
